import useResponsive from "libs/helpers/useResponsive";

const useTaskOnboardingRender = (
  step,
  setStep,
  onClose,
  current_referrer,
  validate,
  task_id,
  task_status,
) => {
  const { max } = useResponsive();

  switch (step) {
    case 0:
      return {
        description: [
          {
            type: "text",
            content:
              "We will guide you through the process creating a task related to your website. First, click on 'CREATE NEW TASK'.",
          },
        ],
        element: {
          target: "onboarding_button",
        },
      };
    case 1:
      return {
        description: [
          {
            type: "text",
            content:
              "Select the website the task refers to. If you have only one website in the Webinity panel, it will be selected automatically.",
          },
        ],
        buttons: [
          {
            label: "Previous step",
            href: `${current_referrer}?onboarding=new`,
          },
          {
            label: "Next step",
            onClick: async () => {
              const errors = await validate("website");

              if (!!!errors?.website) {
                setStep((prev) => prev + 1);
              }
            },
          },
        ],
        element: {
          target: "website_select",
        },
      };
    case 2:
      return {
        description: [
          {
            type: "text",
            content:
              "In this field, you can enter a title for your task. The title should be simple and clear. It is also required to create the task.",
          },
        ],
        buttons: [
          {
            label: "Previous step",
            onClick: () => setStep((prev) => prev - 1),
          },
          {
            label: "Next step",
            onClick: async () => {
              const errors = await validate("title");

              if (!!!errors?.title) {
                setStep((prev) => prev + 1);
              }
            },
          },
        ],
        element: {
          target: "website_title",
        },
      };
    case 3:
      return {
        description: [
          {
            type: "text",
            content:
              "Depending on how urgent the task you want to add is, you can set one of four priority levels. By default, each task has a normal priority. You can find detailed information about priorities",
          },
          {
            type: "button",
            content: "here",
            action: "PRIORITY_INFO",
          },
          {
            type: "text",
            content: ".",
          },
        ],
        buttons: [
          {
            label: "Previous step",
            onClick: () => setStep((prev) => prev - 1),
          },
          {
            label: "Next step",
            onClick: () => setStep((prev) => prev + 1),
          },
        ],
        element: {
          target: "website_priority",
          placement: max.lg ? "bottom-left" : "right",
        },
      };
    case 4:
      return {
        description: [
          {
            type: "text",
            content:
              "The status indicates the stage of progress your task is in. When creating a new task, you should set the status to 'To do'. Other statuses can be used in specific situations, which you can learn more about",
          },
          {
            type: "button",
            content: "here",
            action: "STATUS_INFO",
          },
          {
            type: "text",
            content: ".",
          },
        ],
        buttons: [
          {
            label: "Previous step",
            onClick: () => setStep((prev) => prev - 1),
          },
          {
            label: "Next step",
            onClick: () => setStep((prev) => prev + 1),
          },
        ],
        element: {
          target: "website_status",
          placement: max.lg ? "bottom-left" : "right",
        },
      };
    case 5:
      return {
        description: [
          {
            type: "text",
            content:
              "If completing the task requires sending files, you can attach them here—either while creating the task or during its execution. After the task is created, you’ll also be able to add files to your comments in discussions related to that specific task.",
          },
        ],
        buttons: [
          {
            label: "Previous step",
            onClick: () => setStep((prev) => prev - 1),
          },
          {
            label: "Next step",
            onClick: () => setStep((prev) => prev + 1),
          },
        ],
        element: {
          target: "website_files",
          placement: max.lg ? "top" : "right",
        },
      };
    case 6:
      return {
        description: [
          {
            type: "text",
            content:
              "The better you describe the scope of the task, the faster and more efficiently we’ll be able to complete it. Provide as many detailed pieces of information as possible.",
          },
        ],
        buttons: [
          {
            label: "Previous step",
            onClick: () => setStep((prev) => prev - 1),
          },
          {
            label: "Next step",
            onClick: async () => {
              const errors = await validate("description");

              if (!!!errors?.description) {
                setStep((prev) => prev + 1);
              }
            },
          },
        ],
        element: {
          target: "website_description",
          placement: max.lg ? "top" : "left",
        },
      };
    case 7:
      return {
        description: [
          {
            type: "text",
            content:
              "When the task is complete, simply click the 'Create task' button. The task will be added to the system, and our team will be notified about it.",
          },
        ],
        buttons: [
          {
            label: "Previous step",
            onClick: () => setStep((prev) => prev - 1),
          },
        ],
        element: {
          target: "website_submit",
          placement: "top-right",
        },
      };
    case 8:
      return {
        description: [
          {
            type: "text",
            content: `Each created task is added to a list that contains basic information about it. You can view more details and options for a specific task by clicking the 'View${max.lg ? " details" : ""}' button.`,
          },
        ],
        buttons: [
          {
            label: "",
          },
          {
            label: "Next step",
            href: `/tasks/${task_id}?onboarding=task${current_referrer ? `&referrer=${current_referrer}` : ""}`,
          },
        ],
        element: {
          target: "website_created",
          offset: 0,
          placement: max.lg ? "right" : null,
        },
      };
    case 9:
      return {
        description: [
          {
            type: "text",
            content:
              "Detailed information about the task. They have the same functions as when the task was created. Additionally, you can track the time spent on completing the task.",
          },
        ],
        buttons: [
          {
            label: "Previous step",
            href: `/tasks?onboarding_created=${task_id}${task_status === 6 ? "&status=closed" : ""}`,
          },
          {
            label: "Next step",
            onClick: () => setStep((prev) => prev + 1),
          },
        ],
        element: {
          target: "task_info",
          placement: max.lg ? null : "right",
        },
      };
    case 10:
      return {
        description: [
          {
            type: "text",
            content:
              "This section records all activities related to the task. You can also use it to communicate with the person working on the task.",
          },
        ],
        buttons: [
          {
            label: "Previous step",
            onClick: () => setStep((prev) => prev - 1),
          },
          {
            label: "Close guide",
            onClick: (value) => onClose(value),
          },
        ],
        element: {
          target: "task_chat",
          placement: max.lg ? "top" : "left",
        },
      };
    default:
      return null;
  }
};

const task_onboarding_action = (action, setExplanation) => {
  if (typeof setExplanation !== "function") return;

  switch (action) {
    case "PRIORITY_INFO":
      setExplanation("priority");
      break;
    case "STATUS_INFO":
      setExplanation("status");
      break;
    default:
      return;
  }
};

export { task_onboarding_action };

export default useTaskOnboardingRender;
